/**
 * The pool a member can actually use.
 *
 * Every other amenity in this product has a page a MEMBER opens — My
 * Golf, My Courts, My Boating, My Shooting. Aquatics had The Deck and
 * Administration, both of which refuse a member, and nothing else. At
 * most clubs the pool is the busiest thing they own, and it was the one
 * amenity whose members could only reach it by telephone.
 *
 * Three things on one screen, in the order somebody standing at the
 * gate needs them.
 *
 * THE WAIVER IS FIRST, and it is first for a reason that is not
 * cosmetic: 0183's gate refuses a check-in without a current signature,
 * so a member whose waiver has lapsed is turned away at the gate having
 * seen nothing to warn them. A page that put their bookings first and
 * the waiver at the bottom would be a page that tells them the one
 * thing that matters after they have stopped reading.
 *
 * The version is NOT chosen here. The button posts an empty body and
 * the route resolves whatever is in force, because 0183's supersede
 * rule is that an old signature stops being enough — a screen that sent
 * a version it had read a moment ago could sign a superseded one and
 * pass a gate it should not.
 *
 * A CLOSED POOL SAYS SO, on the facility, before the booking form. The
 * closure reason comes from `fn_pool_closure_reason` rather than from
 * an absence of sessions: "closed for chemical treatment until 2pm" and
 * "nothing scheduled" look identical on a screen that only draws what
 * is on, and they are completely different answers to somebody deciding
 * whether to put the children in the car.
 */

import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { BylawNotices, Card, Empty, ErrorBanner, Loading, Status } from '../components/ui.tsx';
import { clubToday, dayLabel, shiftDate, hhmm } from '../lib/sheet.ts';
import { date as fmtDate } from '../lib/format.ts';

/* Shapes — mirroring the API's JSON one for one. */

interface Facility {
  id: string;
  code: string;
  name: string;
  kind: string;
  lane_count: number | null;
  closed_because: string | null;
}

interface Session {
  id: string;
  facility_id: string;
  facility: string;
  swim_date: string;
  starts_at: string;
  ends_at: string;
  purpose: string;
  status: string;
}

interface Lane {
  id: string;
  swim_date: string;
  starts_at: string;
  minutes: number;
  status: string;
  facility: string;
  area: string | null;
  swimmer: string | null;
}

interface Enrolment {
  id: string;
  status: string;
  swimmer_name: string | null;
  program: string;
  starts_on: string;
  ends_on: string | null;
}

interface Area {
  id: string;
  facility_id: string;
  code: string;
  name: string;
  kind: string;
  capacity: number | null;
}

interface HouseholdMember {
  person_id: string;
  display_name: string;
  is_minor: boolean;
}

interface MyPool {
  facilities: Facility[];
  areas: Area[];
  sessions: Session[];
  my_lanes: Lane[];
  enrolments: Enrolment[];
  household: HouseholdMember[];
  waiver: string | null;
}

/**
 * `fn_pool_waiver_status` answers with a word. The screen has to say
 * what that word means for the person holding the phone, because
 * "expired" and "superseded" are the same practical fact — you will be
 * turned away at the gate — and only one of them is the member's fault.
 */
function waiverTone(
  status: string | null,
): { tone: 'good' | 'warning' | 'serious'; says: string } {
  // `fn_pool_waiver_status` says 'covered', 'expired', 'superseded' or
  // 'unsigned'. The first draft of this function guessed 'current' and
  // 'none' — so a member whose waiver was perfectly in order was shown
  // the word "covered" as a WARNING beside a "Sign the waiver" button
  // and a sentence telling them the gate would ask. It fell through the
  // `default` arm, which is exactly what a default arm is for and
  // exactly why it should never be the arm that runs.
  //
  // A guessed vocabulary is invisible: every branch renders, nothing
  // throws, and the screen is confidently wrong. Read off the function.
  switch (status) {
    case 'covered':
      return { tone: 'good', says: 'Signed and current' };
    case 'expired':
      return { tone: 'warning', says: 'Ran out — sign it again' };
    case 'superseded':
      return { tone: 'warning', says: 'The club has revised it — sign the new one' };
    case 'unsigned':
    case null:
      return { tone: 'serious', says: 'Not signed' };
    default:
      return { tone: 'warning', says: status };
  }
}

/**
 * `pool_session.purpose` is 'lap', 'open_swim', 'lesson' or 'team'.
 *
 * Printed raw, a member read "open_swim" on their own club's screen.
 * The vocabulary is short, closed and the club's own, so it is
 * translated here rather than left to look like a database column.
 */
const PURPOSE_SAYS: Record<string, string> = {
  lap: 'Lap swim',
  open_swim: 'Open swim',
  lesson: 'Lessons',
  team: 'Swim team',
};

export function MyPoolPage() {
  const { club } = useSession();

  // The club's day, not this browser's. A member on holiday in another
  // zone opening the app at eleven at night should see the club's
  // tomorrow only when it is the club's tomorrow.
  const [day, setDay] = useState(() => clubToday(club?.timezone));

  // One request answers the whole screen: the water, the lanes, the
  // household, the bookings and the waiver. This is opened on a phone
  // at a gate, and each extra round trip is a second of a spinner in
  // front of somebody with wet children.
  const pool = useApi<MyPool>(`/v1/me/pool?date=${day}`, [day]);

  const [facility, setFacility] = useState<string>('');
  const [session, setSession] = useState<string>('');
  const [area, setArea] = useState<string>('');
  const [startsAt, setStartsAt] = useState('07:00');
  const [minutes, setMinutes] = useState(30);
  const [swimmer, setSwimmer] = useState<string>('');

  const sign = useMutation(async () =>
    // No version, no document id. The route resolves what is in force.
    api('/v1/me/pool/waiver', { method: 'POST', body: {} }));

  const book = useMutation(async () =>
    api('/v1/me/pool/lanes', {
      method: 'POST',
      body: {
        session_id: session,
        area_id: area,
        starts_at: startsAt,
        minutes,
        ...(swimmer ? { swimmer_person_id: swimmer } : {}),
      },
    }));

  const giveBack = useMutation(async (id: string) =>
    api(`/v1/me/pool/lanes/${id}/cancel`, { method: 'POST', body: {} }));

  const facilities = pool.data?.facilities ?? [];
  const sessions = pool.data?.sessions ?? [];
  const chosenFacility = facility || facilities[0]?.id || '';
  const openSessions = sessions.filter((s) => s.facility_id === chosenFacility);
  const lanes = (pool.data?.areas ?? []).filter((a) => a.facility_id === chosenFacility);
  const closure = facilities.find((f) => f.id === chosenFacility)?.closed_because ?? null;

  const waiver = waiverTone(pool.data?.waiver ?? null);
  const needsSigning = waiver.tone !== 'good';

  async function refresh() {
    pool.reload();
  }

  return (
    <>
      <BylawNotices surface="pool" />

      {/* ============================================================= */}
      {/* The waiver, first, because the gate checks it. */}
      <Card title="Before You Swim">
        <ErrorBanner error={sign.error} />
        {pool.loading ? (
          <Loading rows={1} />
        ) : (
          <div className="row row--wrap" style={{ alignItems: 'center', gap: 12 }}>
            <Status tone={waiver.tone}>{waiver.says}</Status>
            {needsSigning && (
              <>
                <span className="muted small">
                  The gate asks for this at every visit. Signing here is the
                  same signature the desk would take on paper.
                </span>
                <button
                  disabled={sign.busy}
                  onClick={async () => {
                    if (await sign.run()) await refresh();
                  }}
                >
                  {sign.busy ? 'Signing…' : 'Sign the waiver'}
                </button>
              </>
            )}
          </div>
        )}
      </Card>

      {/* ============================================================= */}
      <Card title="The Water Today">
        <ErrorBanner error={pool.error} />

        <div className="row row--wrap" style={{ gap: 8, marginBottom: 12 }}>
          <button className="secondary small" onClick={() => setDay(shiftDate(day, -1))}>
            ‹
          </button>
          <strong>{dayLabel(day, club?.timezone)}</strong>
          <button className="secondary small" onClick={() => setDay(shiftDate(day, 1))}>
            ›
          </button>
          <div className="topbar__spacer" />
          <select value={chosenFacility} onChange={(e) => {
            setFacility(e.target.value);
            setSession('');
            setArea('');
          }}>
            {facilities.map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
        </div>

        {pool.loading ? (
          <Loading rows={3} />
        ) : facilities.length === 0 ? (
          <Empty>This club has no pool on the system yet.</Empty>
        ) : closure ? (
          /* Why it is shut, not just that nothing is on. */
          <Status tone="serious">Closed — {closure}</Status>
        ) : openSessions.length === 0 ? (
          <Empty>Nothing scheduled here on this day.</Empty>
        ) : (
          <table className="data data--cards">
            <thead>
              <tr>
                <th>What</th>
                <th style={{ width: 200 }}>When</th>
                <th style={{ width: 120 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {openSessions.map((s) => (
                <tr key={s.id}>
                  <td>{PURPOSE_SAYS[s.purpose] ?? s.purpose}</td>
                  <td className="mono">{hhmm(s.starts_at)} – {hhmm(s.ends_at)}</td>
                  <td>
                    {s.status === 'scheduled'
                      ? <Status tone="good">On</Status>
                      : <Status tone="warning">{s.status}</Status>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* ============================================================= */}
      {/* Booking is hidden while the water is shut rather than offered
          and refused: the trigger would refuse it (POOL-25), and a form
          that submits into a known refusal is a form that teaches
          people the software is unreliable. */}
      {!closure && lanes.length > 0 && openSessions.length > 0 && (
        <Card title="Take a Lane">
          <ErrorBanner error={book.error} />
          <div className="row row--wrap" style={{ gap: 8, alignItems: 'flex-end' }}>
            <label>
              <span className="muted small">Session</span>
              <select value={session} onChange={(e) => setSession(e.target.value)}>
                <option value="">Choose…</option>
                {openSessions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.purpose} · {hhmm(s.starts_at)}–{hhmm(s.ends_at)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span className="muted small">Lane</span>
              <select value={area} onChange={(e) => setArea(e.target.value)}>
                <option value="">Choose…</option>
                {lanes.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </label>
            <label>
              <span className="muted small">From</span>
              <input
                type="time"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
              />
            </label>
            <label>
              <span className="muted small">For</span>
              <select value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
                {[30, 45, 60, 90].map((m) => (
                  <option key={m} value={m}>{m} minutes</option>
                ))}
              </select>
            </label>
            {/* Only the caller's own household appears here, and the
                route checks it again — the list is a convenience, the
                refusal is the rule. */}
            <label>
              <span className="muted small">Who is swimming</span>
              <select value={swimmer} onChange={(e) => setSwimmer(e.target.value)}>
                <option value="">Me</option>
                {(pool.data?.household ?? []).map((p) => (
                  <option key={p.person_id} value={p.person_id}>{p.display_name}</option>
                ))}
              </select>
            </label>
            <button
              disabled={book.busy || !session || !area}
              onClick={async () => {
                if (await book.run()) await refresh();
              }}
            >
              {book.busy ? 'Booking…' : 'Book it'}
            </button>
          </div>
        </Card>
      )}

      {/* ============================================================= */}
      <Card title="My Lanes" flush>
        <ErrorBanner error={giveBack.error} />
        {(pool.data?.my_lanes ?? []).length === 0 ? (
          <Empty>Nothing booked.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 130 }}>Day</th>
                <th style={{ width: 100 }}>From</th>
                <th>Where</th>
                <th>Who</th>
                <th style={{ width: 120 }} />
              </tr>
            </thead>
            <tbody>
              {(pool.data?.my_lanes ?? []).map((l) => (
                <tr key={l.id}>
                  <td>{fmtDate(l.swim_date)}</td>
                  <td className="mono">{hhmm(l.starts_at)} · {l.minutes}m</td>
                  <td>{l.facility}{l.area ? ` · ${l.area}` : ''}</td>
                  <td>{l.swimmer ?? '—'}</td>
                  <td>
                    <button
                      className="secondary small"
                      disabled={giveBack.busy}
                      onClick={async () => {
                        if (await giveBack.run(l.id)) await refresh();
                      }}
                    >
                      Give it back
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* ============================================================= */}
      {(pool.data?.enrolments ?? []).length > 0 && (
        <Card title="Lessons and Squads" flush>
          <table className="data">
            <thead>
              <tr>
                <th>Programme</th>
                <th>Swimmer</th>
                <th style={{ width: 130 }}>Runs</th>
                <th style={{ width: 120 }}>Place</th>
              </tr>
            </thead>
            <tbody>
              {(pool.data?.enrolments ?? []).map((e) => (
                <tr key={e.id}>
                  <td>{e.program}</td>
                  <td>{e.swimmer_name ?? '—'}</td>
                  <td className="mono small">
                    {e.starts_on}{e.ends_on ? ` – ${e.ends_on}` : ''}
                  </td>
                  <td>
                    {e.status === 'enrolled'
                      ? <Status tone="good">Enrolled</Status>
                      : <Status tone="warning">Waiting list</Status>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}
